// src/services/sharepoint.service.js
class SharePointService {
  constructor(accessToken, siteId) {
    this.accessToken = accessToken;
    this.siteId = siteId;
    this.baseUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}`;
  }

  async graphRequest(url, options = {}) {
  try {
    console.log(`[GraphAPI] ${options.method || 'GET'} ${url}`);
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    console.log(`[GraphAPI] Response status: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[GraphAPI] Error response:`, errorText);
      
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = `HTTP ${response.status}: ${errorJson.error?.message || errorText}`;
      } catch (e) {
        errorMessage = `HTTP ${response.status}: ${errorText}`;
      }
      
      throw new Error(errorMessage);
    } 

    // DELETE requests haben keinen Body
    if (options.method === 'DELETE') {
      console.log('[GraphAPI] DELETE successful (no response body)');
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('[GraphAPI] Request failed:', url, error);
    throw error;
  }
}

  // SharePoint Listen-IDs
  static LIST_IDS = {
    employees: '31f4d427-ddc8-4786-8eba-c23795b5d1dc',
    customers: '6bd1c151-b53e-4e59-9452-1af0e230eec4',
    projects: '2f1e9c79-91ea-4854-8495-ecc94b435573',
    activities: '575b5e30-1220-4fab-9dea-add697560ed8',
    timeEntries: 'bc560a5c-c91d-46e7-a55e-f5a1daf7cb63',
    // TODO: SharePoint-Liste "ProjectAssignments" erstellen (Spalten: Title, EmployeeLookupId, ProjectLookupId)
    // und die echte List-ID hier eintragen:
    projectAssignments: null
  };

  // ─── Projektzuordnungen ────────────────────────────────────────────────────
  // Speicherung als JSON-Datei in SharePoint Documents-Bibliothek.
  // Keine extra SP-Liste nötig — funktioniert tenant-weit für alle User.
  // Fallback auf localStorage wenn SP nicht erreichbar.

  _assignmentsFileUrl() {
    // Speichert in: /sites/<site>/Shared Documents/timetracker-assignments.json
    return `${this.baseUrl}/drive/root:/timetracker-assignments.json`;
  }

  async _readAssignmentsFile() {
    try {
      const url = `${this._assignmentsFileUrl()}:/content`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${this.accessToken}` }
      });
      if (response.status === 404) return [];
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.warn('SP-Datei nicht lesbar, nutze localStorage:', err.message);
      return JSON.parse(localStorage.getItem('projectAssignments') || '[]');
    }
  }

  async _writeAssignmentsFile(assignments) {
    try {
      const url = `${this._assignmentsFileUrl()}:/content`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(assignments)
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      // Immer auch lokal cachen damit der nächste Ladevorgang schnell ist
      localStorage.setItem('projectAssignments', JSON.stringify(assignments));
      return true;
    } catch (err) {
      console.warn('SP-Datei nicht schreibbar, nutze localStorage:', err.message);
      localStorage.setItem('projectAssignments', JSON.stringify(assignments));
      return false;
    }
  }

  async getProjectAssignments() {
    // 1. Versuche SharePoint-Datei (geteilt zwischen allen Usern)
    const fromSP = await this._readAssignmentsFile();
    if (fromSP.length > 0) {
      // Lokalen Cache aktualisieren
      localStorage.setItem('projectAssignments', JSON.stringify(fromSP));
      return fromSP;
    }
    // 2. Falls SP leer → prüfe ob localStorage Daten hat (Migration)
    const local = JSON.parse(localStorage.getItem('projectAssignments') || '[]');
    if (local.length > 0) {
      // Einmalige Migration: lokale Daten nach SP hochladen
      console.log('Migriere localStorage-Zuordnungen nach SharePoint...');
      await this._writeAssignmentsFile(local);
    }
    return local;
  }

  async assignEmployeeToProject(employeeId, projectId) {
    const assignments = await this._readAssignmentsFile();
    // Doppelte verhindern
    const alreadyExists = assignments.some(
      a => Number(a.employeeId) === Number(employeeId) && Number(a.projectId) === Number(projectId)
    );
    if (alreadyExists) return null;
    const newEntry = {
      id: Date.now(),
      employeeId: Number(employeeId),
      projectId: Number(projectId)
    };
    await this._writeAssignmentsFile([...assignments, newEntry]);
    return newEntry;
  }

  async removeEmployeeFromProject(assignmentId) {
    const assignments = await this._readAssignmentsFile();
    const filtered = assignments.filter(a => a.id !== assignmentId);
    await this._writeAssignmentsFile(filtered);
  }

  async getEmployees() {
  try {
    console.log('Loading employees from Graph API...');
    const listId = SharePointService.LIST_IDS.employees;
    const url = `${this.baseUrl}/lists/${listId}/items?expand=fields&$top=100`;
    
    const data = await this.graphRequest(url);
    
    const employees = data.value.map(item => ({
      id: item.fields.Id || item.id,
      name: item.fields.Title || item.fields.MitarbeiterName,
      email: item.fields.Email,
      role: item.fields.Role,
      hourlyRate: item.fields.HourlyRate || 0,
      status: item.fields.Status || 'Active'  // ← Sollte schon da sein!
    }));

    console.log('✅ Employees loaded from SharePoint:', employees.length, 'items');
    return employees;
  } catch (error) {
    console.error('❌ Error loading employees:', error);
    console.log('Using fallback demo data');
    return this.getFallbackEmployees();
  }
}

  async getCustomers() {
  try {
    console.log('Loading customers from Graph API...');
    const listId = SharePointService.LIST_IDS.customers;
    const url = `${this.baseUrl}/lists/${listId}/items?expand=fields&$top=100`;
    
    const data = await this.graphRequest(url);
    
    const customers = data.value.map(item => ({
      id: item.fields.Id || item.id,
      name: item.fields.Title || item.fields.Kundenname,  // Kundenname
      contactName: item.fields.ContactName || '',  // ← NEU!
      contactEmail: item.fields.ContactEmail || '',  // ← NEU!
      status: item.fields.Status || 'Active',
      customerSince: item.fields.CustomerSince ? item.fields.CustomerSince.split('T')[0] : ''  // ← NEU!
    }));

    console.log('✅ Customers loaded from SharePoint:', customers.length, 'items');
    console.log('Sample customer:', customers[0]);  // Debug-Log
    return customers;
  } catch (error) {
    console.error('❌ Error loading customers:', error);
    return this.getFallbackCustomers();
  }
}

  async getProjects() {
  try {
    console.log('Loading projects from Graph API...');
    const listId = SharePointService.LIST_IDS.projects;
    const url = `${this.baseUrl}/lists/${listId}/items?expand=fields&$top=100`;
    
    const data = await this.graphRequest(url);
    
    // Customer Lookup auflösen
    const customers = await this.getCustomers();
    
    const projects = data.value.map(item => {
      const customerLookupId = item.fields.CustomerLookupId;
      const customer = customers.find(c => c.id === customerLookupId);
      
      return {
        id: item.fields.Id || item.id,
        name: item.fields.Title,
        customer: customer?.name || '',
        type: item.fields.ProjectType || 'Fixed Price',  // ← GEÄNDERT!
        status: item.fields.Status || 'Active',
        rate: item.fields.Rate || 0,
        budget: item.fields.Budget || 0
      };
    });

    console.log('✅ Projects loaded from SharePoint:', projects.length, 'items');
    return projects;
  } catch (error) {
    console.error('❌ Error loading projects:', error);
    return this.getFallbackProjects();
  }
}

  async getActivities() {
    try {
      console.log('Loading activities from Graph API...');
      const listId = SharePointService.LIST_IDS.activities;
      const url = `${this.baseUrl}/lists/${listId}/items?expand=fields&$top=100`;
      
      const data = await this.graphRequest(url);
      
      const activities = data.value.map(item => ({
        id: item.fields.id,
        title: item.fields.Title,
        category: item.fields.Category || 'General'
      }));

      console.log('✅ Activities loaded from SharePoint:', activities.length, 'items');
      return activities;
    } catch (error) {
      console.error('❌ Error loading activities:', error);
      return this.getFallbackActivities();
    }
  }

  async getTimeEntries(employeeId = null) {
  try {
    console.log('Loading time entries from Graph API...');
    const listId = SharePointService.LIST_IDS.timeEntries;
    let url = `${this.baseUrl}/lists/${listId}/items?expand=fields&$top=500`;
    
    if (employeeId) {
      url += `&$filter=fields/EmployeeLookupId eq ${employeeId}`;
    }
    
    const data = await this.graphRequest(url);
    
    // Projects, Activities UND Employees laden für Lookup-Auflösung
    const projects = await this.getProjects();
    const activities = await this.getActivities();
    const employees = await this.getEmployees();  // ← NEU!
    
    const timeEntries = data.value.map(item => {
      // Lookup-IDs auflösen
      const project = projects.find(p => p.id === item.fields.ProjectLookupId);
      const activity = activities.find(a => a.id === item.fields.ActivityLookupId);
      const employee = employees.find(e => e.id === item.fields.EmployeeLookupId);  // ← NEU!
      
      return {
        id: item.fields.Id || item.id,
        date: item.fields.EntryDate ? item.fields.EntryDate.split('T')[0] : '',
        project: project?.name || '',
        activity: activity?.title || '',
        hours: parseFloat(item.fields.Hours) || 0,
        billable: item.fields.Billable || false,
        description: item.fields.Description || '',
        employee: employee?.name || '',  // ← GEÄNDERT: Lookup aufgelöst!
        employeeEmail: employee?.email || ''  // ← GEÄNDERT: Lookup aufgelöst!
      };
    });

    console.log('✅ TimeEntries loaded from SharePoint:', timeEntries.length, 'items');
    return timeEntries;
  } catch (error) {
    console.error('❌ Error loading time entries:', error);
    return this.getFallbackTimeEntries();
  }
}

  async saveTimeEntry(entry) {
  try {
    console.log('Saving time entry to Graph API...', entry);
    const listId = SharePointService.LIST_IDS.timeEntries;
    const url = `${this.baseUrl}/lists/${listId}/items`;
    
    // Projekt-ID finden
    const projects = await this.getProjects();
    const project = projects.find(p => p.name === entry.project);
    
    // Activity-ID finden
    const activities = await this.getActivities();
    const activity = activities.find(a => a.title === entry.activity);
    
    // Employee-ID finden
    const employees = await this.getEmployees();
    const employee = employees.find(e => e.email === entry.employeeEmail);
    
    if (!project) {
      throw new Error(`Project "${entry.project}" not found`);
    }
    if (!activity) {
      throw new Error(`Activity "${entry.activity}" not found`);
    }
    if (!employee) {
      throw new Error(`Employee with email "${entry.employeeEmail}" not found`);
    }
    
    const body = {
      fields: {
        Title: `${entry.project} - ${entry.activity} - ${entry.date}`,
        EntryDate: entry.date,
        ProjectLookupId: project.id,
        ActivityLookupId: activity.id,
        EmployeeLookupId: employee.id,  // ← NUR Lookup-ID!
        Hours: entry.hours,
        Billable: entry.billable,
        Description: entry.description || ''
        // ❌ ENTFERNT: Employee, EmployeeEmail (existieren nicht!)
      }
    };

    console.log('Sending to SharePoint:', body);

    const result = await this.graphRequest(url, {
      method: 'POST',
      body: JSON.stringify(body)
    });

    console.log('✅ Time entry saved:', result.id);
    return result;
  } catch (error) {
    console.error('❌ Error saving time entry:', error);
    throw error;
  }
}

  async updateTimeEntry(itemId, entry) {
  try {
    console.log('Updating time entry...', itemId, entry);
    const listId = SharePointService.LIST_IDS.timeEntries;
    const url = `${this.baseUrl}/lists/${listId}/items/${itemId}/fields`;
    
    // Projekt-ID finden
    const projects = await this.getProjects();
    const project = projects.find(p => p.name === entry.project);
    
    // Activity-ID finden
    const activities = await this.getActivities();
    const activity = activities.find(a => a.title === entry.activity);
    
    if (!project) {
      throw new Error(`Project "${entry.project}" not found`);
    }
    if (!activity) {
      throw new Error(`Activity "${entry.activity}" not found`);
    }
    
    const body = {
      Title: `${entry.project} - ${entry.activity} - ${entry.date}`,
      EntryDate: entry.date,  // ← GEÄNDERT!
      ProjectLookupId: project.id,  // ← GEÄNDERT: Lookup-ID!
      ActivityLookupId: activity.id,  // ← GEÄNDERT: Lookup-ID!
      Hours: entry.hours,
      Billable: entry.billable,
      Description: entry.description || ''
      // ❌ KEIN Employee/EmployeeEmail (existieren nicht)
    };

    console.log('Updating with data:', body);

    const result = await this.graphRequest(url, {
      method: 'PATCH',
      body: JSON.stringify(body)
    });

    console.log('✅ Time entry updated');
    return result;
  } catch (error) {
    console.error('❌ Error updating time entry:', error);
    throw error;
  }
}

  async deleteTimeEntry(itemId) {
  try {
    console.log('=== DELETE TIME ENTRY ===');
    console.log('ItemId:', itemId, 'Type:', typeof itemId);
    
    const listId = SharePointService.LIST_IDS.timeEntries;
    const url = `${this.baseUrl}/lists/${listId}/items/${itemId}`;
    
    console.log('DELETE URL:', url);
    
    await this.graphRequest(url, {
      method: 'DELETE'
    });

    console.log('✅ Time entry deleted successfully');
  } catch (error) {
    console.error('❌ Error deleting time entry:', error);
    console.error('Error details:', error.message);
    throw error;
  }
}

  async getCurrentUser() {
    try {
      const url = 'https://graph.microsoft.com/v1.0/me';
      const user = await this.graphRequest(url);
      return {
        name: user.displayName,
        email: user.mail || user.userPrincipalName
      };
    } catch (error) {
      console.error('Error getting current user:', error);
      return { name: 'Unknown', email: '' };
    }
  }

  async getCurrentEmployee(email) {
    const normalizedEmail = (email || '').toLowerCase().trim();

    // 1. Versuche Graph /me für den aktuellen User (braucht keine SP-Berechtigung)
    let meProfile = null;
    try {
      const meData = await this.graphRequest('https://graph.microsoft.com/v1.0/me?$select=id,displayName,mail,userPrincipalName,jobTitle,department');
      meProfile = {
        graphId: meData.id,
        name: meData.displayName,
        email: (meData.mail || meData.userPrincipalName || '').toLowerCase().trim(),
        jobTitle: meData.jobTitle,
        department: meData.department
      };
      console.log('[getCurrentEmployee] Graph /me:', meProfile);
    } catch (err) {
      console.warn('[getCurrentEmployee] Graph /me fehlgeschlagen:', err.message);
    }

    // 2. SP-Employees-Liste laden und abgleichen
    let employees = [];
    try {
      employees = await this.getEmployees();
    } catch (err) {
      console.warn('[getCurrentEmployee] Employees-Liste nicht lesbar:', err.message);
    }

    // 3. Match via E-Mail (SP-Liste zuerst, dann Graph /me als Fallback-Profil)
    const found = employees.find(e => (e.email || '').toLowerCase().trim() === normalizedEmail);
    if (found) return found;

    // 4. Wenn in SP-Liste nicht gefunden aber /me verfügbar: synthetischen Employee erstellen
    // (Employee kann sich einloggen, hat keine Admin-Rechte, sieht nur seine Projekte)
    if (meProfile && meProfile.email === normalizedEmail) {
      console.warn(`[getCurrentEmployee] Employee "${email}" nicht in SP-Liste — nutze Graph-Profil als Employee.`);
      return {
        id: meProfile.graphId,
        name: meProfile.name,
        email: meProfile.email,
        role: 'Employee',
        hourlyRate: 0,
        status: 'Active'
      };
    }

    console.warn(`[getCurrentEmployee] Kein Employee für "${email}" gefunden. Verfügbare E-Mails:`, employees.map(e => e.email));
    return employees[0];
  }

  // Fallback Demo Data
  getFallbackEmployees() {
    return [
      { id: 1, name: 'Adrian Matt', email: 'adrian.matt@travelbrain.ch', role: 'Admin', hourlyRate: 150, status: 'Active' },
      { id: 2, name: 'Demo User', email: 'demo@travelbrain.ch', role: 'Employee', hourlyRate: 100, status: 'Active' }
    ];
  }

  getFallbackCustomers() {
  return [
    { 
      id: 1, 
      name: 'TravelBrain GmbH', 
      contactName: 'Adrian Matt',
      contactEmail: 'contact@travelbrain.ch', 
      status: 'Active',
      customerSince: '2006-02-06'
    },
    { 
      id: 2, 
      name: 'Demo Customer', 
      contactName: 'Demo Contact',
      contactEmail: 'demo@example.com', 
      status: 'Active',
      customerSince: '2020-01-01'
    }
  ];
}

  getFallbackProjects() {
    return [
      { id: 1, name: 'TimeTracker Pro', customer: 'TravelBrain GmbH', type: 'Fixed Price', status: 'Active', rate: 150, budget: 50000 }
    ];
  }

  getFallbackActivities() {
    return [
      { id: 1, title: 'Development', category: 'Technical' },
      { id: 2, title: 'Meeting', category: 'Management' },
      { id: 3, title: 'Planning', category: 'Management' },
      { id: 4, title: 'Testing', category: 'Technical' }
    ];
  }

  getFallbackTimeEntries() {
    return [];
  }
}

// Statische Methoden für direkten Zugriff
SharePointService.getProjects = async function() {
  const instance = window.msalInstance;
  const account = instance.getAllAccounts()[0];
  
  if (!account) throw new Error('No account found');

  const response = await instance.acquireTokenSilent({
    scopes: ['Sites.Read.All'],
    account: account
  });

  const siteId = "travelbrain.sharepoint.com,bb4ae473-be73-4e32-a759-b4b3ae23eaa1,ee97eb85-4115-4b78-9760-bede03b8d4a8";
  const service = new SharePointService(response.accessToken, siteId);
  return service.getProjects();
};

SharePointService.getCustomers = async function() {
  const instance = window.msalInstance;
  const account = instance.getAllAccounts()[0];
  const response = await instance.acquireTokenSilent({
    scopes: ['Sites.Read.All'],
    account: account
  });
  const siteId = "travelbrain.sharepoint.com,bb4ae473-be73-4e32-a759-b4b3ae23eaa1,ee97eb85-4115-4b78-9760-bede03b8d4a8";
  const service = new SharePointService(response.accessToken, siteId);
  return service.getCustomers();
};

SharePointService.getActivities = async function() {
  const instance = window.msalInstance;
  const account = instance.getAllAccounts()[0];
  const response = await instance.acquireTokenSilent({
    scopes: ['Sites.Read.All'],
    account: account
  });
  const siteId = "travelbrain.sharepoint.com,bb4ae473-be73-4e32-a759-b4b3ae23eaa1,ee97eb85-4115-4b78-9760-bede03b8d4a8";
  const service = new SharePointService(response.accessToken, siteId);
  return service.getActivities();
};

SharePointService.getEmployees = async function() {
  const instance = window.msalInstance;
  const account = instance.getAllAccounts()[0];
  const response = await instance.acquireTokenSilent({
    scopes: ['Sites.Read.All'],
    account: account
  });
  const siteId = "travelbrain.sharepoint.com,bb4ae473-be73-4e32-a759-b4b3ae23eaa1,ee97eb85-4115-4b78-9760-bede03b8d4a8";
  const service = new SharePointService(response.accessToken, siteId);
  return service.getEmployees();
};

SharePointService.getTimeEntries = async function() {
  const instance = window.msalInstance;
  const account = instance.getAllAccounts()[0];
  const response = await instance.acquireTokenSilent({
    scopes: ['Sites.Read.All'],
    account: account
  });
  const siteId = "travelbrain.sharepoint.com,bb4ae473-be73-4e32-a759-b4b3ae23eaa1,ee97eb85-4115-4b78-9760-bede03b8d4a8";
  const service = new SharePointService(response.accessToken, siteId);
  return service.getTimeEntries();
};

SharePointService.saveTimeEntry = async function(entry) {
  const instance = window.msalInstance;
  const account = instance.getAllAccounts()[0];
  const response = await instance.acquireTokenSilent({
    scopes: ['Sites.ReadWrite.All'],
    account: account
  });
  const siteId = "travelbrain.sharepoint.com,bb4ae473-be73-4e32-a759-b4b3ae23eaa1,ee97eb85-4115-4b78-9760-bede03b8d4a8";
  const service = new SharePointService(response.accessToken, siteId);
  return service.saveTimeEntry(entry);
};

SharePointService.getCurrentUser = async function() {
  const instance = window.msalInstance;
  const account = instance.getAllAccounts()[0];
  const response = await instance.acquireTokenSilent({
    scopes: ['User.Read'],
    account: account
  });
  const service = new SharePointService(response.accessToken, '');
  return service.getCurrentUser();
};

SharePointService.getProjectAssignments = async function() {
  const instance = window.msalInstance;
  const account = instance.getAllAccounts()[0];
  const response = await instance.acquireTokenSilent({
    scopes: ['Sites.ReadWrite.All'],
    account: account
  });
  const siteId = "travelbrain.sharepoint.com,bb4ae473-be73-4e32-a759-b4b3ae23eaa1,ee97eb85-4115-4b78-9760-bede03b8d4a8";
  const service = new SharePointService(response.accessToken, siteId);
  return service.getProjectAssignments();
};

SharePointService.assignEmployeeToProject = async function(employeeId, projectId) {
  const instance = window.msalInstance;
  const account = instance.getAllAccounts()[0];
  const response = await instance.acquireTokenSilent({
    scopes: ['Sites.ReadWrite.All'],
    account: account
  });
  const siteId = "travelbrain.sharepoint.com,bb4ae473-be73-4e32-a759-b4b3ae23eaa1,ee97eb85-4115-4b78-9760-bede03b8d4a8";
  const service = new SharePointService(response.accessToken, siteId);
  return service.assignEmployeeToProject(employeeId, projectId);
};

SharePointService.removeEmployeeFromProject = async function(assignmentId) {
  const instance = window.msalInstance;
  const account = instance.getAllAccounts()[0];
  const response = await instance.acquireTokenSilent({
    scopes: ['Sites.ReadWrite.All'],
    account: account
  });
  const siteId = "travelbrain.sharepoint.com,bb4ae473-be73-4e32-a759-b4b3ae23eaa1,ee97eb85-4115-4b78-9760-bede03b8d4a8";
  const service = new SharePointService(response.accessToken, siteId);
  return service.removeEmployeeFromProject(assignmentId);
};

export default SharePointService;